import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '../components/Toast';
import PhoneActionModal from '../components/PhoneActionModal';
import { getMemberById } from '../lib/members';

export default function MemberDetails() {
  const { showToast } = useToast();
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      try {
        const data = await getMemberById(id);
        setMember(data);
      } catch (err) {
        console.error('[member details] failed:', err);
        showToast('Failed to load member details', { kind: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) run();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/members" className="text-NBNA-primary hover:underline">← Back to Members</Link>
        <p className="mt-6 text-gray-700">Member not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link to="/members" className="text-NBNA-primary hover:underline">← Back to Members</Link>
      </div>

      {/* Header */}
      <div className="mt-6 overflow-hidden rounded-lg bg-white shadow-sm border">
        <div className="p-6">
          <div className="flex flex-col gap-6">
            {member.businessLogo ? (
              <div className="flex justify-center md:justify-start">
                <div className="h-28 w-28 overflow-hidden rounded-lg bg-transparent">
                  <img src={member.businessLogo} alt="Business logo" className="h-full w-full object-contain" />
                </div>
              </div>
            ) : null}

            <div>
              <h1 className="text-3xl font-semibold text-[#0E4069]">{member.businessName}</h1>
              <p className="mt-1 text-gray-600">{member.name}</p>
              {member.shortDescription ? (
                <p className="mt-4 text-gray-700 max-w-3xl">{member.shortDescription}</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Business Details */}
      <section className="mt-6 bg-white shadow-md rounded-lg p-6">
        <h3 className="mb-4">Business Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Business Name</div>
            <div className="text-gray-800">{member.businessName || '—'}</div>
          </div>

          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Website</div>
            <div className="text-gray-800">
              {member.website ? (
                <a className="text-NBNA-primary hover:underline" href={member.website} target="_blank" rel="noreferrer">
                  {member.website}
                </a>
              ) : (
                '—'
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Email</div>
            <div className="text-gray-800">
              {member.email ? (
                <a className="text-NBNA-primary hover:underline" href={`mailto:${member.email}`}>{member.email}</a>
              ) : (
                '—'
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Phone</div>
            <div className="text-gray-800">
              {member.phone ? (
                <button
                  type="button"
                  className="text-NBNA-primary hover:underline"
                  onClick={() => setPhoneModalOpen(true)}
                >
                  {member.phone}
                </button>
              ) : (
                '—'
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Descriptions */}
      <section className="mt-6 bg-white shadow-md rounded-lg p-6">
        <h3 className="mb-4">Business Description</h3>
        {member.longDescription ? (
          <p className="text-gray-700 whitespace-pre-line">{member.longDescription}</p>
        ) : (
          <p className="text-gray-500">No description provided.</p>
        )}
      </section>

      {/* Business Goals */}
      <section className="mt-6 bg-white shadow-md rounded-lg p-6">
        <h3 className="mb-4">Business Goals</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Goals for Next 6 Months</div>
            <div className="text-gray-700 whitespace-pre-line">{member.sixMonthGoals || '—'}</div>
          </div>
          <div>
            <div className="text-sm font-lato text-NBNA-primary mb-1">Ideal Referral Type</div>
            <div className="text-gray-700 whitespace-pre-line">{member.idealReferral || '—'}</div>
          </div>
        </div>
      </section>
      <PhoneActionModal
        isOpen={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        phone={member.phone}
      />
    </div>
  );
}

